import { useState } from 'react';
import { useAuthStore } from '../../stores';
import AdminLayout from './AdminLayout';
import { AlertTriangle, Upload, Trash2, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import JSZip from 'jszip';

export default function AdminPOIImport() {
  const { token } = useAuthStore();
  const [file, setFile] = useState(null);
  const [importMode, setImportMode] = useState('upsert'); // 'upsert' or 'replace'
  const [categoryFilter, setCategoryFilter] = useState(''); // Category to replace/upsert
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResult(null);
      setError(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const validExtensions = ['.json', '.geojson', '.zip'];
      const hasValidExtension = validExtensions.some(ext => droppedFile.name.toLowerCase().endsWith(ext));
      if (hasValidExtension) {
        setFile(droppedFile);
        setResult(null);
        setError(null);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setResult(null);

    try {
      let fileContent;

      // Handle ZIP files
      if (file.name.toLowerCase().endsWith('.zip')) {
        const arrayBuffer = await file.arrayBuffer();
        const zip = await JSZip.loadAsync(arrayBuffer);
        
        // Find the first JSON file in the ZIP
        const jsonFile = Object.keys(zip.files).find(name => 
          name.toLowerCase().endsWith('.json') && !zip.files[name].dir
        );
        
        if (!jsonFile) {
          throw new Error('No JSON file found in ZIP archive');
        }
        
        fileContent = await zip.files[jsonFile].async('string');
      } else {
        // Regular JSON file
        fileContent = await file.text();
      }

      // Strip BOM if present (UTF-8 BOM: 0xFEFF)
      if (fileContent.charCodeAt(0) === 0xFEFF) {
        fileContent = fileContent.slice(1);
      }

      const data = JSON.parse(fileContent);

      // Detect format and convert to standardized POI array
      let pois = [];

      // Format 1: OCPI-style (Greek EV Charger format from electrokinisi.yme.gov.gr)
      if (data.Locations && Array.isArray(data.Locations)) {
        pois = convertOCPIToPOIs(data.Locations);
      }
      // Format 2: Direct POI array
      else if (Array.isArray(data)) {
        pois = data.map(item => standardizePOI(item));
      }
      // Format 3: GeoJSON FeatureCollection
      else if (data.features && Array.isArray(data.features)) {
        pois = data.features.map(feature => ({
          name: feature.properties.name,
          category: feature.properties.category || 'ev_charger',
          subcategory: feature.properties.subcategory,
          description: feature.properties.description,
          location: {
            type: 'Point',
            coordinates: feature.geometry.coordinates
          },
          address: feature.properties.address,
          metadata: feature.properties.metadata || {}
        }));
      }
      else {
        throw new Error('Unsupported file format. Expected OCPI Locations, POI array, or GeoJSON FeatureCollection');
      }

      if (pois.length === 0) {
        throw new Error('No valid POIs found in file');
      }

      // Send to API in batches for large files
      const batchSize = 500;
      let totalInserted = 0;
      let totalUpdated = 0;
      let totalDeleted = 0;
      const allErrors = [];

      for (let i = 0; i < pois.length; i += batchSize) {
        const batch = pois.slice(i, i + batchSize);
        
        const response = await fetch('https://navigation-gr-api.telenavis-com-account.workers.dev/api/pois/import', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            mode: i === 0 ? importMode : 'upsert', // Only delete on first batch if replace mode
            categoryFilter: categoryFilter || undefined, // Optional: limit to specific category
            pois: batch
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Import failed');
        }

        const batchResult = await response.json();
        totalInserted += batchResult.inserted || 0;
        totalUpdated += batchResult.updated || 0;
        if (i === 0) totalDeleted = batchResult.deleted || 0;
        if (batchResult.errors) {
          allErrors.push(...batchResult.errors);
        }
      }

      setResult({
        inserted: totalInserted,
        updated: totalUpdated,
        deleted: totalDeleted,
        total: pois.length,
        errors: allErrors.length > 0 ? allErrors : undefined
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Convert OCPI format (electrokinisi.yme.gov.gr) to standard POI format
  const convertOCPIToPOIs = (locations) => {
    const pois = [];
    
    locations.forEach(location => {
      if (!location.publish || location.publish === false) return;
      
      // Get all unique connectors from all EVSEs
      const allConnectors = [];
      const evseStatuses = [];
      
      if (location.evses && Array.isArray(location.evses)) {
        location.evses.forEach(evse => {
          if (evse.status) evseStatuses.push(evse.status);
          if (evse.connectors && Array.isArray(evse.connectors)) {
            evse.connectors.forEach(connector => {
              allConnectors.push({
                standard: connector.standard,
                power: connector.max_electric_power,
                type: connector.power_type,
                format: connector.format
              });
            });
          }
        });
      }

      // Determine overall status
      const hasAvailable = evseStatuses.some(s => s === 'AVAILABLE');
      const status = hasAvailable ? 'available' : 
                    evseStatuses.length > 0 ? evseStatuses[0].toLowerCase() : 'unknown';

      // Create POI
      pois.push({
        name: location.name || `${location.party_id} - ${location.id}`,
        category: 'ev_charger',
        subcategory: location.party_id || 'Unknown',
        description: `${location.address || ''} ${location.city || ''}`.trim(),
        location: {
          type: 'Point',
          coordinates: [
            parseFloat(location.coordinates?.longitude),
            parseFloat(location.coordinates?.latitude)
          ]
        },
        address: `${location.address || ''}, ${location.postal_code || ''} ${location.city || ''}`.trim(),
        metadata: {
          ocpi_id: location.id,
          party_id: location.party_id,
          country: location.country,
          parking_type: location.parking_type,
          status: status,
          evse_count: location.evses?.length || 0,
          connectors: allConnectors,
          total_power: allConnectors.reduce((sum, c) => sum + (c.power || 0), 0),
          connector_types: [...new Set(allConnectors.map(c => c.standard))].join(', ')
        }
      });
    });
    
    return pois;
  };

  // Standardize various POI formats
  const standardizePOI = (item) => {
    return {
      name: item.name,
      category: item.category || 'ev_charger',
      subcategory: item.subcategory,
      description: item.description,
      location: item.location || {
        type: 'Point',
        coordinates: [item.lng || item.longitude, item.lat || item.latitude]
      },
      address: item.address,
      metadata: item.metadata || {}
    };
  };

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Import POIs</h1>
        <p className="text-gray-600 mt-2">Bulk import POIs from JSON or GeoJSON files</p>
      </div>

      {/* Import Mode Selection */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Import Configuration</h2>
        
        {/* Import Mode */}
        <div className="mb-6">
          <h3 className="text-md font-medium text-gray-800 mb-3">Import Mode</h3>
          <div className="space-y-4">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="importMode"
                value="upsert"
                checked={importMode === 'upsert'}
                onChange={(e) => setImportMode(e.target.value)}
                className="mt-1"
              />
              <div>
                <div className="flex items-center space-x-2">
                  <RefreshCw size={20} className="text-blue-600" />
                  <span className="font-semibold text-gray-900">Upsert (Update or Insert)</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Update existing POIs with matching names/locations, insert new ones.
                  Existing POIs not in the file are preserved.
                </p>
              </div>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="importMode"
                value="replace"
                checked={importMode === 'replace'}
                onChange={(e) => setImportMode(e.target.value)}
                className="mt-1"
              />
              <div>
                <div className="flex items-center space-x-2">
                  <Trash2 size={20} className="text-red-600" />
                  <span className="font-semibold text-gray-900">Replace (Delete & Insert)</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Delete existing POIs in selected category, then insert new ones.
                  {!categoryFilter && ' ⚠️ Without category filter, this will delete ALL POIs!'}
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Category Filter */}
        <div className="border-t pt-6">
          <h3 className="text-md font-medium text-gray-800 mb-3">Category Filter (Optional)</h3>
          <p className="text-sm text-gray-600 mb-3">
            Limit import operations to a specific category. In replace mode, only POIs in this category will be deleted.
            In upsert mode, only POIs in this category will be updated.
          </p>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Categories (No Filter)</option>
            <option value="ev_charger">⚡ EV Chargers</option>
            <option value="metro">Ⓜ️ Metro Stations</option>
            <option value="rail">🚂 Rail Stations</option>
            <option value="tram">🚊 Tram Stops</option>
            <option value="parking">🅿️ Parking</option>
            <option value="bike_station">🚲 Bike Stations</option>
          </select>
          {categoryFilter && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Active Filter:</strong> Only <strong>{categoryFilter}</strong> POIs will be affected by this import.
                All other categories will remain unchanged.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* File Upload Area */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload File</h2>
        
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-500 transition-colors cursor-pointer"
          onClick={() => document.getElementById('fileInput').click()}
        >
          <Upload size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-700 font-medium mb-2">
            {file ? file.name : 'Click to upload or drag & drop'}
          </p>
          <p className="text-sm text-gray-500">
            JSON, GeoJSON or ZIP files (max 50MB)
          </p>
          <input
            id="fileInput"
            type="file"
            accept=".json,.geojson,.zip"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {file && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-600">{(file.size / 1024).toFixed(2)} KB</p>
              </div>
              <button
                onClick={() => setFile(null)}
                className="text-red-600 hover:text-red-700"
              >
                Remove
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Warning for Replace Mode */}
      {importMode === 'replace' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start space-x-3">
          <AlertTriangle size={24} className="text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-semibold text-red-900">Warning: Destructive Operation</h3>
            <p className="text-sm text-red-700 mt-1">
              {categoryFilter ? (
                <>
                  Replace mode will permanently delete all existing <strong>{categoryFilter}</strong> POIs before importing.
                  Other categories will remain unchanged. This action cannot be undone.
                </>
              ) : (
                <>
                  Replace mode will permanently delete <strong>ALL existing POIs in all categories</strong> before importing.
                  This action cannot be undone. Please ensure you have a backup if needed.
                </>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Import Button */}
      <div className="flex justify-end space-x-4">
        <button
          onClick={() => {
            setFile(null);
            setResult(null);
            setError(null);
          }}
          disabled={isProcessing}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Clear
        </button>
        <button
          onClick={handleImport}
          disabled={!file || isProcessing}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center space-x-2"
        >
          {isProcessing ? (
            <>
              <RefreshCw size={20} className="animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              <Upload size={20} />
              <span>Import POIs</span>
            </>
          )}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <CheckCircle size={24} className="text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-green-900 text-lg">Import Successful!</h3>
              <div className="mt-4 space-y-2 text-sm text-green-800">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded p-3">
                    <p className="text-gray-600">Inserted</p>
                    <p className="text-2xl font-bold text-green-600">{result.inserted || 0}</p>
                  </div>
                  <div className="bg-white rounded p-3">
                    <p className="text-gray-600">Updated</p>
                    <p className="text-2xl font-bold text-blue-600">{result.updated || 0}</p>
                  </div>
                  {result.deleted !== undefined && (
                    <div className="bg-white rounded p-3">
                      <p className="text-gray-600">Deleted</p>
                      <p className="text-2xl font-bold text-red-600">{result.deleted}</p>
                    </div>
                  )}
                  <div className="bg-white rounded p-3">
                    <p className="text-gray-600">Total Processed</p>
                    <p className="text-2xl font-bold text-gray-900">{result.total || 0}</p>
                  </div>
                </div>
                {result.errors && result.errors.length > 0 && (
                  <div className="mt-4">
                    <p className="font-semibold text-yellow-800">Errors:</p>
                    <ul className="list-disc list-inside text-yellow-700">
                      {result.errors.map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <XCircle size={24} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 text-lg">Import Failed</h3>
              <p className="text-red-700 mt-2">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Format Examples */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Supported File Formats</h3>
        
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-800 mb-2">1. Greek EV Charger Format (OCPI - electrokinisi.yme.gov.gr):</h4>
            <pre className="bg-gray-800 text-green-400 p-4 rounded text-xs overflow-x-auto">
{`{
  "Locations": [
    {
      "country_code": "GR",
      "party_id": "PPC",
      "id": "GR-PPC-Station-123",
      "publish": true,
      "name": "Athens Central Station",
      "address": "Syntagma Square",
      "city": "Athens",
      "postal_code": "10563",
      "coordinates": {
        "latitude": "37.9755",
        "longitude": "23.7353"
      },
      "parking_type": "ON_STREET",
      "evses": [
        {
          "uid": "GR-PPC-E001",
          "status": "AVAILABLE",
          "connectors": [
            {
              "standard": "IEC_62196_T2",
              "power_type": "AC_3_PHASE",
              "max_electric_power": 22000
            }
          ]
        }
      ]
    }
  ]
}`}
            </pre>
            <p className="text-sm text-gray-600 mt-2">
              ✅ Supports ZIP files (e.g., GR.IDRO.static.data.latest.json.zip)<br/>
              ✅ Automatically converts to EV charger POIs with metadata<br/>
              ✅ Handles large files (30MB+) with batch processing
            </p>
          </div>

          <div>
            <h4 className="font-medium text-gray-800 mb-2">2. JSON Array Format:</h4>
            <pre className="bg-gray-800 text-green-400 p-4 rounded text-xs overflow-x-auto">
{`[
  {
    "name": "Σταθμός Μετρό Συντάγματος",
    "category": "metro",
    "subcategory": "station",
    "description": "Κεντρικός σταθμός μετρό",
    "location": {
      "type": "Point",
      "coordinates": [23.7353, 37.9755]
    },
    "metadata": {
      "line": "Μ2",
      "accessibility": true
    }
  }
]`}
            </pre>
          </div>

          <div>
            <h4 className="font-medium text-gray-800 mb-2">3. GeoJSON FeatureCollection:</h4>
            <pre className="bg-gray-800 text-green-400 p-4 rounded text-xs overflow-x-auto">
{`{
  "type": "FeatureCollection",
  "features": [
    {
      "type": "Feature",
      "geometry": {
        "type": "Point",
        "coordinates": [23.7353, 37.9755]
      },
      "properties": {
        "name": "Σταθμός Μετρό Συντάγματος",
        "category": "metro",
        "subcategory": "station",
        "description": "Κεντρικός σταθμός μετρό",
        "metadata": {
          "line": "Μ2",
          "accessibility": true
        }
      }
    }
  ]
}`}
            </pre>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
